import { type FC, type ReactNode } from 'react';
import { useHistory, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { type RootState } from '@editor2/reducers/root';
import {
	getChapterByPageGroupId,
	getChapterByPageId,
	getParentEntityByPageId
} from '@editor2/components/CourseChangesDiff/helpers';

interface Props {
	courseId: number;
	children: ReactNode;
}

const RoutingCheckWrapper: FC<Props> = (props) => {
	const { courseId, children } = props;

	const history = useHistory();
	const { chapters, page_groups } = useSelector((state: RootState) => state.courses[courseId]);

	const getIdFromPathname = (entity) => {
		const params = history.location.pathname.split('/');
		return params.includes(entity) ? params[params.indexOf(entity) + 1] : null;
	};

	const chapterId = getIdFromPathname('chapters');
	const pageGroupId = getIdFromPathname('page_groups');
	const pageId = getIdFromPathname('pages');

	const getElementsPagePath = () => {
		const parentChapter = getChapterByPageId(chapters, page_groups, pageId);
		const parentPageGroup = getParentEntityByPageId(page_groups, pageId);

		return (
			`/courses/${courseId}/chapters/${parentChapter.family_id}` +
			(parentPageGroup ? `/page_groups/${parentPageGroup.family_id}` : '') +
			`/pages/${pageId}`
		);
	};

	const getPageGroupPagePath = () => {
		const parentChapter = getChapterByPageGroupId(chapters, pageGroupId);
		return `/courses/${courseId}/chapters/${parentChapter.family_id}/page_groups/${pageGroupId}`;
	};

	const getCurrentPath = () => {
		switch (true) {
			case !!pageId:
				return getElementsPagePath();

			case !!pageGroupId:
				return getPageGroupPagePath();

			default:
				break;
		}
	};

	const isRouteCorrect = () => {
		switch (true) {
			case !!pageId: {
				return pageGroupId
					? page_groups.entities[pageGroupId].pages?.includes(pageId) &&
							chapters.entities[chapterId].page_groups?.includes(pageGroupId)
					: chapters.entities[chapterId].pages?.includes(pageId);
			}

			case !!pageGroupId:
				return chapters.entities[chapterId].page_groups?.includes(pageGroupId);

			default:
				break;
		}

		return true;
	};

	return (
		<>
			{isRouteCorrect() && children}

			{!isRouteCorrect() && <Redirect to={getCurrentPath()} />}
		</>
	);
};

export default RoutingCheckWrapper;
